import React, { useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Card, Row, Col, Tag, Spin, Alert, Button, Progress, Table } from "antd";
import {
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  HourglassOutlined,
  FlagOutlined,
  BankOutlined,
  DollarOutlined,
  AimOutlined,
  FileTextOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
  ContactsOutlined,
  KeyOutlined,
  SearchOutlined,
  SolutionOutlined,
  AuditOutlined,
  FileProtectOutlined,
  HomeOutlined,
} from "@ant-design/icons";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import useProjectDrilldown from "../../hooks/dashboard/useProjectDrilldown";
import "./DrilldownDashboard.css";

// Shared palette
const C = {
  green: "#1a9e5c",
  greenSoft: "#e8f6ee",
  blue: "#2e6fdb",
  blueSoft: "#eaf1fd",
  orange: "#e07b1a",
  orangeSoft: "#fdf1e2",
  red: "#d0392b",
  redSoft: "#fbe9e7",
  gray: "#9aa2ac",
  graySoft: "#eef0f2",
};

const STAGE_ICONS = [
  FlagOutlined,
  FileTextOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
  ContactsOutlined,
  KeyOutlined,
  SearchOutlined,
  SolutionOutlined,
  AuditOutlined,
  FileProtectOutlined,
  HomeOutlined,
  CheckCircleOutlined,
];

const normalize = (s) => (s || "").toString().toLowerCase().trim();

const STATUS_STYLE = {
  completed: { bg: C.greenSoft, fg: C.green, label: "Completed", icon: CheckCircleOutlined },
  "in progress": { bg: C.blueSoft, fg: C.blue, label: "In Progress", icon: ClockCircleOutlined },
  in_progress: { bg: C.blueSoft, fg: C.blue, label: "In Progress", icon: ClockCircleOutlined },
  delayed: { bg: C.orangeSoft, fg: C.orange, label: "Delayed", icon: HourglassOutlined },
  critical: { bg: C.redSoft, fg: C.red, label: "Critical", icon: ExclamationCircleOutlined },
  unidentified: { bg: C.graySoft, fg: C.gray, label: "Unidentified", icon: ClockCircleOutlined },
  pending: { bg: C.graySoft, fg: C.gray, label: "Pending", icon: ClockCircleOutlined },
};

const getStatusStyle = (status) => STATUS_STYLE[normalize(status)] || STATUS_STYLE.pending;

const parsePercent = (v) => {
  if (typeof v === "number") return v;
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

const parseFraction = (v) => {
  if (!v || typeof v !== "string") return { completed: 0, total: 0 };
  const parts = v.split("/").map((p) => parseInt(p.trim(), 10));
  return { completed: parts[0] || 0, total: parts[1] || 0 };
};

// Reusable Components
const StatCard = ({ icon, label, value, accent, note }) => (
  <Card className="dd-stat-card" bordered>
    <div className="dd-stat-row">
      <div className="dd-stat-icon" style={{ background: `${accent}1a`, color: accent }}>
        {icon}
      </div>
      <div className="dd-stat-body">
        <span className="dd-stat-label">{label}</span>
        <span className="dd-stat-value">{value}</span>
        {note && <span className="dd-stat-note">{note}</span>}
      </div>
    </div>
  </Card>
);

const DonutLegendCard = ({ title, data, total }) => (
  <Card className="dd-card dd-donut-card" title={<span className="dd-card-title">{title}</span>}>
    <div className="dd-donut-flex">
      <ResponsiveContainer width="45%" height={190}>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" innerRadius={48} outerRadius={80} paddingAngle={2}>
            {data.map((d, i) => (
              <Cell key={i} fill={d.color} />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <div className="dd-donut-legend">
        {data.map((d, i) => (
          <div className="dd-donut-legend-item" key={i}>
            <span className="dd-dot" style={{ background: d.color }} />
            <span className="dd-donut-legend-name">{d.name}</span>
            <span className="dd-donut-legend-value">{d.value}</span>
          </div>
        ))}
        <div className="dd-donut-total">
          <span>Total Milestones</span>
          <strong>{total}</strong>
        </div>
      </div>
    </div>
  </Card>
);

const MilestoneTracker = ({ stages }) => (
  <Card className="dd-card dd-tracker-card" title={<span className="dd-card-title">Milestone Timeline</span>}>
    <div className="dd-stage-row">
      {stages.map((s, i) => {
        const Icon = STAGE_ICONS[i % STAGE_ICONS.length];
        const style = getStatusStyle(s.status);
        return (
          <div className="dd-stage-chip" key={s.index ?? i} style={{ background: style.bg }}>
            <div className="dd-stage-chip-icon" style={{ color: style.fg }}>
              <Icon />
            </div>
            <div className="dd-stage-chip-label">
              {i + 1}. {s.milestone_name}
            </div>
            <div className="dd-stage-chip-meta">
              <span className="dd-stage-chip-status" style={{ color: style.fg }}>
                {style.label}
              </span>
              {s.date && s.date !== "-" && <span className="dd-stage-chip-date">{s.date}</span>}
              {s.delay_days && s.delay_days !== "-" && (
                <Tag color="error" className="dd-stage-chip-delay-tag">
                  {s.delay_days}
                </Tag>
              )}
            </div>
          </div>
        );
      })}
    </div>
    <div className="dd-tracker-legend">
      {["completed", "in progress", "delayed", "critical", "pending"].map((k) => (
        <span key={k} className="dd-tracker-legend-item">
          <span
            className="dd-dot"
            style={{ background: STATUS_STYLE[k].bg, border: `1px solid ${STATUS_STYLE[k].fg}` }}
          />
          {STATUS_STYLE[k].label}
        </span>
      ))}
    </div>
  </Card>
);

const KeyValueGrid = ({ rows }) => (
  <div className="dd-kv-grid">
    {rows.map((r) => (
      <div className="dd-kv-row" key={r.key}>
        <span className="dd-kv-label">{r.label}</span>
        <span className="dd-kv-value">{r.value || "—"}</span>
      </div>
    ))}
  </div>
);

// New Horizontal Table for Land Acquisition Records
const KeyValueTable = ({ data }) => {
  const columns = [
    {
      title: "Particulars",
      dataIndex: "label",
      key: "label",
      width: "40%",
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: "Details",
      dataIndex: "value",
      key: "value",
      render: (text) => text || "—",
    },
  ];

  const tableData = Object.entries(data || {}).map(([key, value], index) => ({
    key: index,
    label: key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
    value: value,
  }));

  return (
    <Table
      columns={columns}
      dataSource={tableData}
      pagination={false}
      bordered
      size="middle"
      rowClassName="kv-row"
    />
  );
};

const DrilldownDashboard = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const projectId = id || searchParams.get("id");
  const { data, loading, error, fetchDrilldown } = useProjectDrilldown(projectId);

  const donutData = useMemo(() => {
    if (!data?.kpi_summary?.milestone_donut_chart) return [];
    return [
      { name: "Completed", value: data.kpi_summary.milestone_donut_chart.completed || 0, color: C.green },
      { name: "In Progress", value: data.kpi_summary.milestone_donut_chart.in_progress || 0, color: C.blue },
      { name: "Delayed", value: data.kpi_summary.milestone_donut_chart.delayed || 0, color: C.orange },
      { name: "Pending", value: data.kpi_summary.milestone_donut_chart.pending || 0, color: C.gray },
    ].filter((d) => d.value > 0);
  }, [data]);

  const donutTotal = useMemo(() => donutData.reduce((a, d) => a + d.value, 0), [donutData]);

  if (loading) {
    return (
      <div className="dd-loading">
        <Spin size="large" />
        <p>Loading Project Details...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="dd-page">
        <Alert
          message="Failed to Load Project Details"
          description={error?.message || error || "Please try again"}
          type="error"
          showIcon
          action={
            <Button onClick={fetchDrilldown} icon={<ReloadOutlined />}>
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  const {
    header_meta,
    kpi_summary,
    section_progress,
    milestone_timeline,
    delay_analysis,
    acquisition_table_records,
    officer_details,
  } = data;

  const statusStyle = getStatusStyle(header_meta?.overall_status);
  const overallProgress = parsePercent(kpi_summary?.overall_progress);
  const milestoneFraction = parseFraction(kpi_summary?.total_milestones);
  const delayedCount = kpi_summary?.delayed_milestones?.count || 0;
  const delayedDays = kpi_summary?.delayed_milestones?.days_delay || 0;
  const isOnTrack = normalize(delay_analysis?.delay_impact) === "none" && delayedCount === 0;
  const hasUpcoming = (kpi_summary?.upcoming_milestones?.count || 0) > 0;

  const delayRows = [
    { key: "reason", label: "Reason", value: delay_analysis?.delay_reason },
    { key: "action_taken", label: "Action Taken", value: delay_analysis?.action_taken },
    { key: "next_action", label: "Next Action", value: delay_analysis?.next_action },
  ];

  if (delay_analysis?.delayed_since && delay_analysis.delayed_since !== "-") {
    delayRows.push({ key: "delayed_since", label: "Delayed Since", value: delay_analysis.delayed_since });
  }

  return (
    <div className="dd-page">
      {/* Header */}
      <Card className="dd-header-card">
        <div className="dd-header-top">
          <div className="dd-header-left">
            <div className="dd-breadcrumb">
              <EnvironmentOutlined /> {header_meta?.zone || "Vashi Zone"}
              <span className="dd-breadcrumb-sep">•</span>
              {header_meta?.district || "Raigad"}
            </div>
            <h1 className="dd-title">{header_meta?.project_name}</h1>
            <div className="dd-badge-row">
              <Tag
                className="dd-pill"
                style={{ background: statusStyle.bg, color: statusStyle.fg, border: `1px solid ${statusStyle.fg}33` }}
              >
                {statusStyle.label}
              </Tag>
              <Tag className="dd-pill dd-pill-muted">
                <BankOutlined /> {header_meta?.land_type}
              </Tag>
            </div>
          </div>
          <div className="dd-header-right">
            <div className="dd-updated-chip">
              <CalendarOutlined /> Updated {kpi_summary?.last_updated}
            </div>
            <Button className="dd-refresh-btn" type="primary" icon={<ReloadOutlined />} onClick={fetchDrilldown}>
              Refresh
            </Button>
          </div>
        </div>

        <div className="dd-fact-strip">
          <div className="dd-fact">
            <span className="dd-fact-icon" style={{ color: C.blue }}>
              <AimOutlined />
            </span>
            <div>
              <span className="dd-fact-label">Total Area</span>
              <span className="dd-fact-value">{header_meta?.total_area}</span>
            </div>
          </div>
          
          <div className="dd-fact">
            <span className="dd-fact-icon" style={{ color: C.orange }}>
              <UserOutlined />
            </span>
            <div>
              <span className="dd-fact-label">Project Owner</span>
              <span className="dd-fact-value">{header_meta?.project_owner}</span>
            </div>
          </div>
          <div className="dd-fact">
            <span className="dd-fact-icon" style={{ color: C.red }}>
              <CalendarOutlined />
            </span>
            <div>
              <span className="dd-fact-label">Expected Completion</span>
              <span className="dd-fact-value">{kpi_summary?.expected_completion}</span>
            </div>
          </div>
        </div>
      </Card>

      {/* KPI Row */}
      <Row gutter={[20, 20]} className="dd-section" align="stretch">
        <Col xs={24} sm={12} lg={6}>
          <Card className="dd-stat-card dd-progress-card" bordered>
            <span className="dd-stat-label">Overall Progress</span>
            <div className="dd-progress-value-row">
              <span className="dd-stat-value">{overallProgress.toFixed(1)}%</span>
            </div>
            <Progress percent={overallProgress} strokeColor={C.green} showInfo={false} size="small" />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="dd-stat-card dd-progress-card" bordered>
            <span className="dd-stat-label">Milestones Completed</span>
            <div className="dd-progress-value-row">
              <span className="dd-stat-value">
                {milestoneFraction.completed} / {milestoneFraction.total}
              </span>
            </div>
            <Progress
              percent={milestoneFraction.total ? (milestoneFraction.completed / milestoneFraction.total) * 100 : 0}
              strokeColor={C.blue}
              showInfo={false}
              size="small"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            icon={<ExclamationCircleOutlined />}
            label="Delayed Milestones"
            value={delayedCount}
            note={delayedCount > 0 ? `${delayedDays} days delay` : "No delays"}
            accent={delayedCount > 0 ? C.red : C.green}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            icon={<FlagOutlined />}
            label="Next Milestone"
            value={hasUpcoming ? kpi_summary.upcoming_milestones.next_milestone : "All Complete"}
            note={hasUpcoming ? `${kpi_summary.upcoming_milestones.count} upcoming` : undefined}
            accent={C.blue}
          />
        </Col>
      </Row>

      {/* Donut + Section Progress */}
      <Row gutter={[20, 20]} className="dd-section" align="stretch">
        <Col xs={24} lg={12}>
          <DonutLegendCard title="Milestone Breakdown" data={donutData} total={donutTotal} />
        </Col>
        <Col xs={24} lg={12}>
          <Card className="dd-card" title={<span className="dd-card-title">Section-wise Progress</span>}>
            {Object.keys(section_progress || {}).length === 0 ? (
              <div className="dd-empty-state">No section-wise progress has been recorded for this project yet.</div>
            ) : (
              <div className="dd-section-progress-row">
                {Object.entries(section_progress || {}).map(([key, value]) => (
                  <div className="dd-section-item" key={key}>
                    <Progress type="circle" percent={value} size={92} strokeColor={C.red} />
                    <p className="dd-section-name">{key.replace(/_/g, " ").replace("section", "Section")}</p>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Milestone Timeline */}
      <Row gutter={[20, 20]} className="dd-section">
        <Col xs={24}>
          <MilestoneTracker stages={milestone_timeline || []} />
        </Col>
      </Row>

      {/* Land Acquisition Records - Horizontal Table */}
      <Row gutter={[20, 20]} className="dd-section">
        <Col xs={24}>
          <Card className="dd-card" title={<span className="dd-card-title">Land Acquisition Records</span>}>
            <KeyValueTable data={acquisition_table_records} />
          </Card>
        </Col>
      </Row>

      {/* Delay Analysis + Officer Side by Side */}
      <Row gutter={[20, 20]} className="dd-section" align="stretch">
        <Col xs={24} lg={12}>
          {delay_analysis && (
            <Card
              className={`dd-card dd-delay-card ${isOnTrack ? "dd-delay-card--ok" : "dd-delay-card--bad"}`}
              title={<span className="dd-card-title">Delay Analysis</span>}
            >
              <div className="dd-delay-stat-row">
                <div className="dd-delay-stat">
                  <span className="dd-fact-label">Total Delay</span>
                  <span className="dd-delay-stat-value" style={{ color: isOnTrack ? C.green : C.red }}>
                    {delay_analysis.total_delay}
                  </span>
                </div>
                <div className="dd-delay-stat">
                  <span className="dd-fact-label">Impact</span>
                  <span className="dd-delay-stat-value">{delay_analysis.delay_impact}</span>
                </div>
              </div>
              <KeyValueGrid rows={delayRows} />
            </Card>
          )}
        </Col>

        <Col xs={24} lg={12}>
          <Card className="dd-card dd-officer-card" title={<span className="dd-card-title">Land Acquisition Officer</span>}>
            <div className="dd-officer-info">
              <span className="dd-officer-icon">
                <UserOutlined />
              </span>
              <div>
                <h3>{officer_details?.land_acquisition_officer}</h3>
                <p>{officer_details?.civil_circle}</p>
              </div>
            </div>
            <div className="dd-contact-info">
              <div>
                <PhoneOutlined /> {officer_details?.contact}
              </div>
              <div>
                <MailOutlined /> {officer_details?.email}
              </div>
              <div>
                <EnvironmentOutlined /> {officer_details?.subdivision_office}
              </div>
              <div>
                <EnvironmentOutlined /> {officer_details?.civil_division}
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DrilldownDashboard;